import { type CurrencyCode } from "@forgepoint/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface QuoteCurrencySelectorProps {
  /** The quote's customer-facing currency (defaults to base when unset). */
  quoteCurrency: CurrencyCode | null;
  /** The base→customer exchange rate as a decimal string. */
  exchangeRate: string;
  /** The organization's base currency. */
  baseCurrency: CurrencyCode;
  /** Currencies to offer — those with a configured rate from the base. */
  availableCurrencies: CurrencyCode[];
  /** Disables editing (e.g. for finalized quotes). */
  readOnly?: boolean;
  /** Whether a rate is currently being fetched. */
  rateLoading?: boolean;
  onCurrencyChange: (currency: CurrencyCode) => void;
}

/**
 * Currency + exchange-rate controls for a quote, rendered as label/value rows
 * that sit inside the totals card. The rate row — a read-only
 * "1 BASE → rate CUSTOMER" statement — appears only when the chosen currency
 * differs from the base. The rate is not hand-editable; it reflects the active
 * snapshot pinned when the quote is saved.
 * @param props - The selector props
 * @returns The currency selector rows
 */
export function QuoteCurrencySelector({
  quoteCurrency,
  exchangeRate,
  baseCurrency,
  availableCurrencies,
  readOnly = false,
  rateLoading = false,
  onCurrencyChange,
}: QuoteCurrencySelectorProps) {
  const selectedCurrency = quoteCurrency ?? baseCurrency;
  const isBase = selectedCurrency === baseCurrency;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs sm:text-sm text-muted-foreground">
          Currency
        </span>
        <Select
          value={selectedCurrency}
          onValueChange={(value) => {
            const code = availableCurrencies.find((c) => c === value);
            if (code) {
              onCurrencyChange(code);
            }
          }}
          disabled={readOnly}
        >
          <SelectTrigger className="h-7 w-[88px] tabular-nums font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableCurrencies.map((code) => (
              <SelectItem key={code} value={code} className="tabular-nums">
                {code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isBase && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs sm:text-sm text-muted-foreground">
            Exchange rate
          </span>
          <div className="flex items-center gap-1.5 text-sm tabular-nums">
            <span className="text-muted-foreground">1 {baseCurrency}</span>
            <span className="text-muted-foreground/50" aria-hidden>
              &rarr;
            </span>
            {rateLoading ? (
              <span
                className="inline-block size-3.5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
                role="status"
                aria-label="Finding exchange rate"
              />
            ) : (
              <span
                className="font-medium"
                aria-label={`Exchange rate from ${baseCurrency} to ${selectedCurrency}`}
              >
                {exchangeRate || "—"}
              </span>
            )}
            <span className="font-medium">{selectedCurrency}</span>
          </div>
        </div>
      )}
    </div>
  );
}
